<template>
    <div class="position-relative">
        <div class="position-relative">
            <MDBBtn @click="toggleNotifications" class="shadow-0 mx-0 px-2">
                <vue-feather type="bell" :stroke-width="2.5" size="20px" class="my-auto text-black" />
            </MDBBtn>
            <MDBBadge v-if="unreadCount > 0" color="danger" class="translate-middle" notification
                style="top: 10px; right: -16px; padding: 6px;">
                {{ unreadCount }}
            </MDBBadge>
        </div>

        <div v-if="notifContent" class="position-absolute bg-white shadow rounded p-3 text-black notif-content">
            <div class="d-flex justify-content-between align-items-center mb-2">
                <p class="fw-bold m-0">Notifications</p>
                <p class="primary-text m-0" style="cursor: pointer; font-size: 13px;" @click.stop="markAllAsRead">
                    <u>Mark all as read</u>
                </p>
            </div>

            <div class="d-flex flex-column gap-2" style="max-height: 50vh; overflow: auto;" @scroll="handleScroll"
                ref="scrollContainer">
                <div v-if="notificationList.length === 0 && !isLoading" class="text-center text-muted py-3">
                    No notifications found.
                </div>

                <div v-for="(notif, index) in notificationList" :key="index"
                    class="d-flex gap-3 align-items-center p-2 rounded-3" :style="{
                        cursor: 'pointer',
                        backgroundColor: notif.isRead ? 'white' : '#D7DEFF',
                    }" @click="markAsRead(notif.id)">

                    <!-- Avatar -->
                    <div v-if="notif.profilePictureUrl" class="flex-shrink-0">
                        <img :src="`${notif.profilePictureUrl}&t=${Date.now()}`" alt="Profile Picture"
                            style="width: 40px; height: 40px; border-radius: 50%; object-fit: cover;" />
                    </div>
                    <div v-else class="flex-shrink-0 d-flex align-items-center justify-content-center text-white"
                        style="width: 40px; height: 40px; border-radius: 50%; background-color: var(--primary); font-weight: bold;">
                        {{ notif.firstName?.charAt(0).toUpperCase() || "?" }}
                    </div>

                    <div class="d-flex flex-column flex-grow-1" style="min-width: 0;">
                        <div class="d-flex justify-content-between align-items-center">
                            <p class="fw-bold mb-0 text-truncate">{{ notif.title }}</p>
                            <div class="d-flex align-items-center gap-1 ms-2">
                                <span class="text-muted" style="font-size: 12px; white-space: nowrap;">
                                    {{ parseDateLong(notif.dateCreated) }}
                                </span>
                            </div>
                        </div>
                        <p class="mb-0 text-muted" style="font-size: 13px; line-height: 1.3;"><span
                                class="fw-bold text-black">{{
                                    notif.firstName }} {{ notif.lastName }}</span> {{ notif.message }}</p>
                    </div>

                    <vue-feather v-if="!notif.isRead" type="check" size="14" class="text-success flex-shrink-0 ps-2" />
                </div>

                <div v-if="isLoading" class="text-center py-2">
                    <div class="spinner-border spinner-border-sm text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </div>

                <div v-if="!hasMore && notificationList.length > 0" class="text-center text-muted py-2"
                    style="font-size: 13px;">
                    No more notifications
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { parseDateLong } from "@/helpers/parseDate";
import { useUserDetailStore } from "@/store/userDetailStore";
import { MDBBadge, MDBBtn } from "mdb-vue-ui-kit";
import { storeToRefs } from "pinia";
import { computed, onMounted, onUnmounted, ref } from "vue";
import VueFeather from "vue-feather";
import { GetPaginatedList, MarkAllAsRead, MarkAsRead } from "../../../services/TransactionNotification";

const userDetailStore = useUserDetailStore();
const { isAdmin, isSuperAdmin } = storeToRefs(userDetailStore);

const notifContent = ref(false);
const page = ref(1);
const perPage = 10;
const notificationList = ref([]);
const isLoading = ref(false);
const hasMore = ref(true);
const scrollContainer = ref(null);

const unreadCount = computed(() => {
    return notificationList.value.filter(notif => !notif.isRead).length;
});

const toggleNotifications = () => {
    notifContent.value = !notifContent.value;
    if (notifContent.value && notificationList.value.length === 0) {
        fetchNotifications(true);
    }
};

onMounted(() => {
    if (isAdmin.value || isSuperAdmin.value) {
        fetchNotifications(true)
    }
})

const fetchNotifications = async (reset = false) => {
    if (isLoading.value) return;

    isLoading.value = true;
    try {
        const currentPage = reset ? 1 : page.value;
        const response = await GetPaginatedList({
            pageNumber: currentPage,
            rowsPerPage: perPage,
        });

        if (response?.data) {
            if (reset) {
                notificationList.value = response.data;
                page.value = 2;
            } else {
                notificationList.value = [...notificationList.value, ...response.data];
                page.value += 1;
            }

            hasMore.value = response.data.length >= perPage;
        } else if (reset) {
            notificationList.value = [];
        }
    } catch (error) {
        console.error("Error fetching notifications:", error);
    } finally {
        isLoading.value = false;
    }
};

const handleScroll = (event) => {
    const element = event.target;
    const bottomReached = element.scrollHeight - element.scrollTop <= element.clientHeight + 100;

    if (bottomReached && !isLoading.value && hasMore.value) {
        fetchNotifications();
    }
};

const markAllAsRead = async () => {
    if (unreadCount.value === 0) return;

    try {
        await MarkAllAsRead();
        notificationList.value = notificationList.value.map(notif => ({
            ...notif,
            isRead: true
        }));
        fetchNotifications();
    } catch (error) {
        console.error("Error marking all as read:", error);
    }
};

const markAsRead = async (notificationId) => {
    const notif = notificationList.value.find((n) => n.id === notificationId);
    if (notif?.isRead) return;

    try {
        await MarkAsRead(notificationId);
        notificationList.value = notificationList.value.map(n =>
            n.id === notificationId ? { ...n, isRead: true } : n
        );
        fetchNotifications();
    } catch (error) {
        console.error("Error marking as read:", error);
    }
};

// Auto-refresh notifications every 30 seconds when dropdown is closed
let refreshInterval;

onMounted(() => {
    refreshInterval = setInterval(() => {
        if (!notifContent.value && notificationList.value.length > 0 && (isAdmin.value || isSuperAdmin.value)) {
            fetchNotifications(true);
        }
    }, 30000);
});

onUnmounted(() => {
    clearInterval(refreshInterval);
});
</script>

<style scoped>
.rounded-5 {
    border-radius: 1rem !important;
}

.notif-content {
    width: 450px;
    right: -10px;
    z-index: 1080 !important;
}

@media (max-width: 767px) {
    .notif-content {
        width: 365px;
        right: -180px;
        z-index: 1080 !important;
    }
}
</style>